"use client"

import React, { useState, useRef, useEffect } from "react"
import Image from "next/image"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/shared/components/ui/dialog"
import { Button } from "@/shared/components/ui/button"
import { Label } from "@/shared/components/ui/label"
import { X, Play, FileText } from "lucide-react"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/components/ui/select"
import {
    galleryIcon,
} from "@/features/work-orders/constants/images"
import { getAllWorkOrders } from "@/features/work-orders/api/work-orders.api"
import { uploadMultiplePhotosDirect } from "@/features/photos/upload-client"
import { UploadBatchProgressIndicator } from "@/features/photos/components/upload-batch-progress"
import type { DirectUploadBatchProgress } from "@/shared/types/direct-upload-progress.type"
import { useSession } from "@/features/auth/context/session.context"
import successToast from "@/shared/lib/toast/success.toast"
import errorToast from "@/shared/lib/toast/error.toast"
import warningToast from "@/shared/lib/toast/warning.toast"

// Stable empty array for default initialFiles; prevents new reference on each render
// which would cause useEffects that depend on initialFiles to loop (Maximum update depth exceeded)
const EMPTY_FILE_ARRAY: File[] = []

// Component to handle individual file preview with proper cleanup
function FilePreviewItem({ file, isVideo, isPdf, onRemove }: { file: File; isVideo: boolean; isPdf?: boolean; onRemove: () => void }) {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)

    useEffect(() => {
        const url = URL.createObjectURL(file)
        setPreviewUrl(url)
        
        return () => {
            URL.revokeObjectURL(url)
        }
    }, [file])

    if (!previewUrl && !isPdf) return null

    return (
        <div className="relative group">
            {isPdf ? (
                <div className="w-full h-20 rounded border overflow-hidden bg-muted flex items-center justify-center gap-2 p-2">
                    <FileText className="w-8 h-8 text-muted-foreground" />
                    <span className="text-xs truncate">{file.name}</span>
                </div>
            ) : isVideo ? (
                <div className="relative w-full h-20 rounded border overflow-hidden bg-black">
                    <video
                        src={previewUrl!}
                        className="w-full h-full object-cover"
                        muted
                    />
                    {/* Video indicator */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                        <Play className="w-8 h-8 text-white fill-white" />
                    </div>
                </div>
            ) : (
                <Image
                    src={previewUrl!}
                    alt={`Preview ${file.name}`}
                    className="w-full h-20 object-cover rounded border"
                    width={80}
                    height={80}
                />
            )}
            <button
                onClick={onRemove}
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 z-10"
            >
                ×
            </button>
        </div>
    )
}

interface AddPhotoModalProps {
    isOpen: boolean
    onClose: () => void
    homeId?: string
    workOrderId?: string // Optional work order ID to pre-select
    onUploadSuccess?: () => void // Callback to refresh photos after successful upload
    initialFiles?: File[] // Files that were already selected before opening the modal
    /** Optional tags (e.g. ['confidential']) for uploaded photos */
    uploadTags?: string[]
}

function AddPhotoModal({ isOpen, onClose, homeId, workOrderId, onUploadSuccess, initialFiles = EMPTY_FILE_ARRAY, uploadTags }: AddPhotoModalProps) {
    const [selectedWorkOrder, setSelectedWorkOrder] = useState<string>(workOrderId || "")
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [teamLeadWorkOrders, setTeamLeadWorkOrders] = useState<any[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState<DirectUploadBatchProgress | null>(null)
    const [filePercents, setFilePercents] = useState<number[]>([])
    const galleryInputRef = useRef<HTMLInputElement>(null)
    const submittedRef = useRef(false)
    
    // Set initial files when modal opens with pre-selected files
    useEffect(() => {
        if (isOpen && initialFiles.length > 0) {
            setSelectedFiles(initialFiles)
        }
    }, [isOpen, initialFiles])

    const { session, profile } = useSession()

    // Fetch work orders where current user is team lead for this home
    // Skip fetching if workOrderId prop is provided (dropdown won't be shown)
    useEffect(() => {
        const fetchTeamLeadWorkOrders = async () => {
            if (session && profile && homeId && !workOrderId) {
                try {
                    // Fetch all work orders
                    const workOrdersResponse = await getAllWorkOrders()

                    if (
                        workOrdersResponse.success &&
                        workOrdersResponse.data?.workOrders
                    ) {
                        // Filter work orders where current user is the team lead AND matches current home ID
                        const filteredWorkOrders =
                            workOrdersResponse.data.workOrders.filter(
                                (workOrder: any) =>
                                    workOrder.teamLead?.id === profile.id &&
                                    workOrder.home.id === parseInt(homeId)
                            )

                        // Filter work orders with "Active" status
                        const activeWorkOrders = filteredWorkOrders
                            .filter(
                                (workOrder: any) =>
                                    workOrder.status === "Active"
                            )
                            .map((workOrder: any) => ({
                                id: workOrder.id,
                                orderNumber: workOrder.orderNumber,
                                workOrderType: workOrder.workOrderType,
                                createdAt: workOrder.createdAt,
                            }))
                            .sort(
                                (
                                    a: { createdAt: string },
                                    b: { createdAt: string }
                                ) =>
                                    new Date(b.createdAt).getTime() -
                                    new Date(a.createdAt).getTime()
                            ) // Sort by most recent first

                        setTeamLeadWorkOrders(activeWorkOrders)
                    }
                } catch (error) {
                    console.error(
                        "Error fetching team lead work orders:",
                        error
                    )
                }
            }
        }

        fetchTeamLeadWorkOrders()
    }, [session, profile, homeId, workOrderId])

    const handleGalleryClick = () => {
        galleryInputRef.current?.click()
    }

    // Helper function to validate files
    const validateFile = (file: File): string | null => {
        // Get file extension (case-insensitive)
        const fileName = file.name.toLowerCase()
        const lastDot = fileName.lastIndexOf('.')
        const fileExtension = lastDot >= 0 ? fileName.substring(lastDot) : ''
        
        // Check for unsupported video formats FIRST (before any other checks)
        // This ensures we show the helpful conversion message and prevent upload
        const unsupportedVideoExtensions = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
        if (unsupportedVideoExtensions.includes(fileExtension)) {
            return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format. You can use free tools like HandBrake or VLC to convert your video.`
        }
        
        // Also check MIME types for unsupported formats (some browsers might not set extension correctly)
        const unsupportedVideoMimeTypes = [
            'video/x-msvideo',      // AVI
            'video/x-matroska',     // MKV
            'video/x-flv',          // FLV
            'video/x-ms-wmv',       // WMV
            'video/3gpp',           // 3GP
        ]
        if (unsupportedVideoMimeTypes.includes(file.type.toLowerCase())) {
            return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format. You can use free tools like HandBrake or VLC to convert your video.`
        }
        
        // Check if it's an image, video, or PDF
        const isImage = file.type.startsWith('image/')
        const isVideo = file.type.startsWith('video/')
        const isPdf = file.type === 'application/pdf' || fileExtension === '.pdf'

        // Also check by extension if MIME type is missing or generic
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.heic', '.heif']
        const videoExtensions = ['.mov', '.mp4', '.webm', '.m4v', '.ts']

        const isImageByExtension = imageExtensions.includes(fileExtension)
        const isVideoByExtension = videoExtensions.includes(fileExtension)

        if (!isImage && !isVideo && !isPdf && !isImageByExtension && !isVideoByExtension) {
            return `File "${file.name}" is not a supported image, video, or PDF format`
        }

        // Check file size limits
        const MAX_IMAGE_SIZE = 50 * 1024 * 1024 // 50MB for images
        const MAX_VIDEO_SIZE = 500 * 1024 * 1024 // 500MB for videos (per plan)
        const MAX_TOTAL_SIZE = 1024 * 1024 * 1024 // 1GB total for all files

        const isActuallyImage = isImage || isImageByExtension
        const isActuallyVideo = isVideo || isVideoByExtension
        const isActuallyPdf = isPdf || fileExtension === '.pdf'
        const MAX_PDF_SIZE = 50 * 1024 * 1024 // 50MB for PDFs

        if (isActuallyImage && file.size > MAX_IMAGE_SIZE) {
            return `Image "${file.name}" is too large. Maximum size is ${MAX_IMAGE_SIZE / 1024 / 1024}MB`
        }

        if (isActuallyVideo && file.size > MAX_VIDEO_SIZE) {
            return `Video "${file.name}" is too large. Maximum size is ${MAX_VIDEO_SIZE / 1024 / 1024}MB`
        }

        if (isActuallyPdf && file.size > MAX_PDF_SIZE) {
            return `PDF "${file.name}" is too large. Maximum size is ${MAX_PDF_SIZE / 1024 / 1024}MB`
        }

        // Check video format support
        if (isActuallyVideo) {
            const supportedVideoTypes = [
                'video/quicktime', // .MOV
                'video/mp4',       // .MP4
                'video/webm',      // .WebM
                'video/x-m4v',     // .M4V
                'video/mp2t',      // .TS (MPEG Transport Stream)
            ]
            
            const supportedExtensions = ['.mov', '.mp4', '.webm', '.m4v', '.ts']
            
            const isSupportedType = supportedVideoTypes.includes(file.type.toLowerCase())
            const isSupportedExtension = supportedExtensions.includes(fileExtension)
            
            if (!isSupportedType && !isSupportedExtension) {
                return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, M4V, or TS format. You can use free tools like HandBrake or VLC to convert your video.`
            }
        }

        return null // File is valid
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (files) {
            const fileArray = Array.from(files)
            
            // Validate each file
            const validFiles: File[] = []
            const errors: string[] = []
            
            // Calculate current total size
            const currentTotalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0)
            const MAX_TOTAL_SIZE = 1024 * 1024 * 1024 // 1GB total
            
            fileArray.forEach((file) => {
                // Check total size limit
                if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
                    errors.push(`Total file size would exceed ${MAX_TOTAL_SIZE / 1024 / 1024}MB limit`)
                    return
                }
                
                // Validate individual file
                const error = validateFile(file)
                if (error) {
                    errors.push(error)
                } else {
                    validFiles.push(file)
                }
            })
            
            // Show errors if any
            if (errors.length > 0) {
                errors.forEach((error) => errorToast(error))
            }
            
            // Add valid files
            if (validFiles.length > 0) {
                setSelectedFiles((prev) => [...prev, ...validFiles])
            }
            
            // Reset input to allow selecting the same file again if needed
            event.target.value = ''
        }
    }

    const handleSubmit = async () => {
        if (submittedRef.current) return
        if (selectedFiles.length === 0) {
            errorToast("Please select at least one photo or video")
            return
        }

        if (!homeId) {
            errorToast("Home ID is required")
            return
        }

        const homeIdNum = typeof homeId === 'string' ? parseInt(homeId, 10) : homeId
        if (isNaN(homeIdNum)) {
            errorToast("Invalid home ID")
            return
        }

        // Get work order ID: use prop if provided, otherwise use selected value
        const workOrderIdToUse = workOrderId 
            ? parseInt(workOrderId, 10)
            : (selectedWorkOrder 
                ? parseInt(selectedWorkOrder, 10) 
                : undefined)

        if (workOrderIdToUse && isNaN(workOrderIdToUse)) {
            errorToast("Invalid work order ID")
            return
        }

        try {
            // Final validation check before upload to prevent body size limit errors
            const unsupportedVideoExtensions = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
            const unsupportedVideoMimeTypes = [
                'video/x-msvideo',      // AVI
                'video/x-matroska',     // MKV
                'video/x-flv',          // FLV
                'video/x-ms-wmv',       // WMV
                'video/3gpp',           // 3GP
            ]
            
            for (const file of selectedFiles) {
                const fileName = file.name.toLowerCase()
                const lastDot = fileName.lastIndexOf('.')
                const fileExtension = lastDot >= 0 ? fileName.substring(lastDot) : ''
                
                // Check for unsupported formats
                if (unsupportedVideoExtensions.includes(fileExtension) || 
                    unsupportedVideoMimeTypes.includes(file.type.toLowerCase())) {
                    errorToast(`Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format. You can use free tools like HandBrake or VLC to convert your video.`)
                    return
                }
            }
            
            submittedRef.current = true
            setIsUploading(true)
            setUploadProgress(null)
            setFilePercents(selectedFiles.map(() => 0))

            // Upload photos using the real API
            const uploadResult = await uploadMultiplePhotosDirect(
                homeIdNum,
                selectedFiles,
                workOrderIdToUse,
                undefined,
                uploadTags?.length ? uploadTags : undefined,
                setUploadProgress,
                (fileIndex, pctWithinFile) => {
                    setFilePercents((prev) => {
                        const base = prev.length === selectedFiles.length ? [...prev] : selectedFiles.map(() => 0)
                        base[fileIndex] = pctWithinFile
                        return base
                    })
                }
            )

            if (uploadResult.success) {
                const uploaded = uploadResult.data ?? []
                const count = uploaded.length
                const errs = ('errors' in uploadResult && Array.isArray(uploadResult.errors))
                    ? uploadResult.errors
                    : []
                const skipped = ('skippedDuplicates' in uploadResult && Array.isArray(uploadResult.skippedDuplicates))
                    ? uploadResult.skippedDuplicates
                    : []

                if (count > 0) {
                    const mediaType = count === 1
                        ? (selectedFiles[0].type.startsWith('video/') ? 'video' : 'photo')
                        : 'items'
                    successToast(`Successfully uploaded ${count} ${mediaType}${count > 1 ? 's' : ''}.`)
                }
                if (skipped.length > 0) {
                    warningToast(
                        `${skipped.length} photo(s) were not added because they already exist in this home's timeline.`
                    )
                }
                if (errs.length > 0) {
                    const detail = errs.slice(0, 2).join(' ')
                    errorToast(`${errs.length} file(s) could not be processed. ${detail}`)
                }

                if (count > 0) {
                    if (onUploadSuccess) onUploadSuccess()
                    onClose()
                }
            } else {
                errorToast(
                    ('message' in uploadResult ? uploadResult.message : undefined) || 
                    `Failed to upload photos. Please try again.`
                )
            }
        } catch (error) {
            console.error("Error uploading photos:", error)
            errorToast(
                error instanceof Error 
                    ? error.message 
                    : "An error occurred while uploading photos. Please try again."
            )
        } finally {
            submittedRef.current = false
            setIsUploading(false)
            setUploadProgress(null)
            setFilePercents([])
        }
    }

    const handleCancel = () => {
        setSelectedWorkOrder("")
        setSelectedFiles([])
        onClose()
    }

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
    }

    // Reset form when modal closes, but preserve workOrderId if provided
    useEffect(() => {
        if (!isOpen) {
            setSelectedWorkOrder(workOrderId || "")
            setUploadProgress(null)
            setFilePercents([])
            // Only clear files if no initial files were provided (user closed without selecting)
            if (initialFiles.length === 0) {
                setSelectedFiles([])
            }
        }
    }, [isOpen, workOrderId, initialFiles])

    // Update selected work order when workOrderId prop changes
    useEffect(() => {
        if (workOrderId) {
            setSelectedWorkOrder(workOrderId)
        }
    }, [workOrderId])

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] max-h-[100vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <DialogHeader>
                    <DialogTitle>Add Photo/Video</DialogTitle>
                    <DialogDescription>
                        {workOrderId 
                            ? "Select photos or videos to upload to this home. These will be associated with the current work order."
                            : "Select photos or videos to upload to this home. You can optionally associate them with a work order."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Work Order Selection (Optional) - Hide when uploadTags (e.g. confidential) is used */}
                    {!workOrderId && !uploadTags?.length && (
                        <div className="space-y-2">
                            <Label htmlFor="work-order">
                                Work Order (Optional)
                            </Label>
                            <div className="relative">
                                <Select
                                    value={selectedWorkOrder}
                                    onValueChange={setSelectedWorkOrder}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a work order" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {teamLeadWorkOrders.length > 0 ? (
                                            teamLeadWorkOrders.map((workOrder) => (
                                                <SelectItem
                                                    key={workOrder.id}
                                                    value={workOrder.id.toString()}
                                                >
                                                    {workOrder.orderNumber}
                                                </SelectItem>
                                            ))
                                        ) : (
                                            <SelectItem value="no-data" disabled>
                                                No active work orders assigned to
                                                you for this home
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                                {selectedWorkOrder && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute right-8 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                                        onClick={() => setSelectedWorkOrder("")}
                                    >
                                        <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Photo/Video Selection */}
                    <div className="space-y-2">
                        <Label>Select Photos/Videos</Label>
                        <div className="flex gap-8 justify-center py-4">
                            {/* Always show gallery option only (camera is handled by FAB) */}
                            <div
                                className="flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={handleGalleryClick}
                            >
                                <Image
                                    src={galleryIcon}
                                    alt="Gallery Icon"
                                    className="w-10 h-10"
                                />
                                <span className="text-sm">Select Photos</span>
                            </div>
                        </div>
                    </div>

                    {/* Hidden file input for gallery selection */}
                    <input
                        ref={galleryInputRef}
                        type="file"
                        accept="image/*,video/*,.pdf,application/pdf"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    {/* Selected files preview */}
                    {selectedFiles.length > 0 && (
                        <div className="space-y-2">
                            <Label>
                                Selected Media ({selectedFiles.length})
                            </Label>
                            <div className="grid grid-cols-3 gap-2 p-2 max-h-64 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                {selectedFiles.map((file, index) => {
                                    const isVideo = file.type.startsWith('video/')
                                    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
                                    return (
                                        <FilePreviewItem
                                            key={`${file.name}-${index}`}
                                            file={file}
                                            isVideo={isVideo}
                                            isPdf={isPdf}
                                            onRemove={() => removeFile(index)}
                                        />
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {isUploading && selectedFiles.length > 0 && (
                    <UploadBatchProgressIndicator
                        progress={
                            uploadProgress ?? {
                                completedCount: 0,
                                totalCount: selectedFiles.length,
                                activeIndex: 1,
                                activeFileName: selectedFiles[0]?.name ?? null,
                            }
                        }
                        fileNames={selectedFiles.map((f) => f.name)}
                        filePercents={filePercents.length === selectedFiles.length ? filePercents : undefined}
                    />
                )}

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        className="w-full"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        className="w-full"
                        disabled={selectedFiles.length === 0 || isUploading}
                        showLoader={isUploading}
                    >
                        {isUploading ? "Uploading..." : "Submit"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default AddPhotoModal
